import { ScrollArea, ScrollBar } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { cn } from "@/core/utils/utils";
import type { ComparisonSheetSummary } from "@/payroll/editor/types";
import { SHEET_STATE } from "./sheet-state";
import { SheetSwitcher } from "./sheet-switcher";

/**
 * The sheets of the payroll, as a strip of tabs.
 *
 * A sheet that exists on only one side is not a cell edit, it is a whole sheet
 * arriving or leaving, so it says which rather than carrying the dot.
 */
export const SheetStrip = ({
  listed,
  all,
  activeIndex,
  onSelect,
}: {
  listed: ComparisonSheetSummary[];
  /** Every sheet, not just the ones the strip is showing. */
  all: ComparisonSheetSummary[];
  activeIndex: number | null;
  onSelect: (index: number) => void;
}) => (
  // On the chrome's ground, not the artifact's. Choosing which sheet to look
  // at is the editor's control, so it sits with the period picker and the run
  // button rather than on the white sheet it chooses.
  // The same 40px band the rows above and below it use, so the chrome stacks
  // evenly instead of stepping.
  //
  // The switcher sits outside the scroll area rather than first inside it. It
  // was first inside, so it scrolled away with the names: the one control that
  // reaches a sheet you cannot see left the screen as soon as you went looking
  // for one.
  <Stack
    gap="none"
    align="stretch"
    direction="row"
    className="flex min-h-10 w-full shrink-0 items-center gap-1 bg-gray-10 py-1 pl-3"
  >
    <SheetSwitcher sheets={all} activeIndex={activeIndex} onSelect={onSelect} />
    <ScrollArea className="min-w-0 flex-1">
      <Stack
        gap="none"
        align="stretch"
        direction="row"
        className="flex items-center gap-1 pr-3"
      >
        {listed.map((entry) => (
          <button
            key={entry.index}
            type="button"
            onClick={() => onSelect(entry.index)}
            className={cn(
              "flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1 text-sm",
              {
                "bg-surface-secondary font-medium": entry.index === activeIndex,
                "text-secondary-text hover:text-primary-text": !(
                  entry.index === activeIndex
                ),
              },
            )}
          >
            {entry.name}
            {/* Renaming a sheet used to read as two sheets, the old name dropped
                  and the new one added with every cell counted as new. They pair
                  by access key now, so this says what actually happened. */}
            {!!entry.renamed_from && (
              <span className="text-sm text-tertiary-text">
                was {entry.renamed_from}
              </span>
            )}
            {/* A sheet that exists on only one side is not a cell edit,
                  it is a new or dropped page of the payroll. */}
            {SHEET_STATE[entry.state] ? (
              <span className="text-sm uppercase tracking-wide text-tertiary-text">
                {SHEET_STATE[entry.state]}
              </span>
            ) : (
              entry.state !== "unchanged" && (
                <span
                  aria-label={`${entry.changed_count} changed`}
                  className="h-1.5 w-1.5 rounded-full bg-stroke-interactive"
                />
              )
            )}
          </button>
        ))}
      </Stack>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  </Stack>
);
